import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./HomeVideoSections.css";

// 🔁 Replace with a real product ID from your system
const KNOWN_WORKING_ID = 1; // Change to a valid product ID

const HomeVideoSections = () => {
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const videoRef = useRef(null);

  // Video state for play/pause
  const [isPlaying, setIsPlaying] = useState(false);
  const [videoError, setVideoError] = useState(false);

  // Fallback product with a valid ID
  const fallbackProduct = {
    id: KNOWN_WORKING_ID,
    name: "Featured Product",
    image: `${process.env.PUBLIC_URL}/images/videopage.webp`,
    shortDescription: "Amazing product for your hair.",
  };

  // Helper to extract ID from product object
  const extractProductId = (productData) => {
    if (!productData) return null;
    const possibleIdFields = ["id", "productId", "product_id", "ID"];
    for (const field of possibleIdFields) {
      if (productData[field] !== undefined) {
        return productData[field];
      }
    }
    return null;
  };

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const baseUrl = process.env.REACT_APP_BASE_URL || "https://volumaire.backendapihub.com";
        const response = await fetch(
          `${baseUrl}/api/complete-products/bestsellers?countryCode=US`
        );
        if (!response.ok) {
          throw new Error(`API error: ${response.status}`);
        }
        const json = await response.json();
        console.log("Bestsellers API response:", json);

        // Extract product array – handle both { data: [...] } and direct array
        const products = json.data || json;
        if (Array.isArray(products) && products.length > 0) {
          const firstProduct = products[0];
          const productId = extractProductId(firstProduct);

          if (productId) {
            setProduct({
              id: productId,
              name: firstProduct.name || fallbackProduct.name,
              image: firstProduct.image || firstProduct.thumbnail || fallbackProduct.image,
              shortDescription: firstProduct.shortDescription || firstProduct.description || fallbackProduct.shortDescription,
            });
          } else {
            console.warn("Product from API has no valid ID. Using fallback.");
            setProduct(fallbackProduct);
          }
        } else {
          console.warn("No products in API response. Using fallback.");
          setProduct(fallbackProduct);
        }
      } catch (err) {
        console.error("Error fetching bestsellers:", err);
        setError(err.message);
        setProduct(fallbackProduct);
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, []);

  // Handle video autoplay and set initial playing state
  useEffect(() => {
    if (!videoRef.current) return;

    const playVideo = async () => {
      try {
        await videoRef.current.play();
        setIsPlaying(true);
        setVideoError(false);
      } catch (err) {
        console.warn("Video autoplay failed:", err);
        setVideoError(true);
        setIsPlaying(false);
      }
    };

    playVideo();

    return () => {
      if (videoRef.current) {
        videoRef.current.pause();
      }
    };
  }, []);

  // Toggle play/pause
  const togglePlay = async () => {
    if (!videoRef.current || videoError) return;

    try {
      if (isPlaying) {
        await videoRef.current.pause();
        setIsPlaying(false);
      } else {
        await videoRef.current.play();
        setIsPlaying(true);
      }
    } catch (err) {
      console.log("Video toggle failed:", err);
      setVideoError(true);
    }
  };

  // Video error handler
  const handleVideoError = () => {
    console.log("Background video failed to load");
    setVideoError(true);
    setIsPlaying(false);
  };

  const handleProductClick = () => {
    if (product?.id) {
      navigate(`/product/${product.id}`);
    } else {
      console.error("No valid product ID to navigate");
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleProductClick();
    }
  };

  const displayProduct = product || fallbackProduct;
  const productImage = displayProduct.image || fallbackProduct.image;
  const productName = displayProduct.name || fallbackProduct.name;
  const productDescription = displayProduct.shortDescription || fallbackProduct.shortDescription;

  return (
    <section className="split-bg-section">
      {/* Left side – image with overlay */}
      <div
        className="bg-half image-bg"
        style={{ backgroundImage: `url(${productImage})` }}
      >
        {/* Clickable area covering the whole image */}
        <div
          className="clickable-area"
          onClick={handleProductClick}
          role="button"
          tabIndex={0}
          onKeyDown={handleKeyDown}
          aria-label={`View ${productName} product details`}
        />

        {/* Overlay content (text + button) */}
        <div className="overlay-box">
          <div className="overlay-content">
            {loading && <p className="info-msg">Loading product...</p>}
            {error && <p className="error-msg">Using default product (API error).</p>}
            <h2>Perfect hair with {productName}</h2>
            <button
              type="button"
              className="check-product-btn"
              onClick={(e) => {
                e.stopPropagation();
                handleProductClick();
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.stopPropagation();
                  handleProductClick();
                }
              }}
              aria-label={`Check ${productName} product`}
              disabled={!product?.id}
            >
              CHECK PRODUCT
            </button>
          </div>
        </div>
      </div>

      {/* Right side – video */}
      <div className="bg-half video-bg">
        <video
          ref={videoRef}
          src="/videos/video1.mp4"
          autoPlay
          loop
          muted
          playsInline
          onError={handleVideoError}
          aria-label="Product demonstration video"
        />
        {/* Play/Pause button – only shown if video loaded successfully */}
        {!videoError && (
          <button
            className={`video-control-btn ${isPlaying ? "pause" : "play"}`}
            onClick={togglePlay}
            aria-label={isPlaying ? "Pause video" : "Play video"}
          />
        )}
      </div>
    </section>
  );
};

export default HomeVideoSections;